import React from 'react';
import { Table, Progress } from 'antd';
import { UserRound, Calendar, CircleCheck, CircleX, Building2, MapPin, Trash2, ClipboardList, MessageSquareText } from 'lucide-react';
import dayjs from 'dayjs';
import { PollQuestions } from '../pollQuestions';
import { rolLabel } from './PollFilters';
import { scoreTone } from './PollAnalytics';
import { SectionCard, StatusTag, IconAction, IconActions, EmptyState } from '../../common';
import { semantic } from '../../../theme/tokens';

const emptyDash = (value) => (value ? value : <span className="text-surface-300">—</span>);

// Renders the full questionnaire for one survey, grouped by section, with each answer
// shown as Sí / No / (sin responder). This is the "desplegable de preguntas por vendedor".
function PollDetail({ record }) {
    const answerByValue = React.useMemo(() => {
        const map = {};
        (record?.values || []).forEach((v) => {
            map[v.value] = v.answer;
        });
        return map;
    }, [record]);

    return (
        <div className="px-2 py-2">
            {record?.comment ? (
                <div className="mb-5 flex gap-2.5 rounded-lg border border-surface-200 bg-white p-3">
                    <MessageSquareText className="mt-0.5 shrink-0 text-surface-400" />
                    <div>
                        <span className="block text-xs font-medium text-surface-500 mb-0.5">Comentario</span>
                        <span className="text-sm text-surface-700">{record.comment}</span>
                    </div>
                </div>
            ) : null}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-10 gap-y-5">
                {PollQuestions.map((section) => (
                    <div key={section.title}>
                        <span className="block text-[13px] font-semibold text-surface-900 mb-2">{section.title}</span>
                        <div className="space-y-1.5">
                            {section.questions.map((q) => {
                                const answer = answerByValue[q.value];
                                const answered = answer === true || answer === false;
                                return (
                                    <div key={q.value} className="flex items-start gap-2">
                                        {answer === true ? (
                                            <CircleCheck className="mt-0.5 shrink-0 text-[14px] text-success-fg" aria-label="Sí" />
                                        ) : answer === false ? (
                                            <CircleX className="mt-0.5 shrink-0 text-[14px] text-danger-fg" aria-label="No" />
                                        ) : (
                                            <span className="mt-0.5 w-[14px] shrink-0 text-center text-surface-300">•</span>
                                        )}
                                        <span className={`text-xs leading-5 ${answered ? 'text-surface-700' : 'text-surface-400'}`}>{q.question}</span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export default function PollTable({ polls, loading, onDelete }) {
    const PollColumns = [
        {
            title: 'Vendedor',
            dataIndex: 'seller',
            key: 'seller',
            render: (text) => (
                <div className="flex items-center gap-3">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-surface-100 text-surface-500">
                        <UserRound className="text-[15px]" />
                    </span>
                    <span className="text-sm font-medium text-surface-900">{emptyDash(text)}</span>
                </div>
            ),
            sorter: (a, b) => (a.seller || '').localeCompare(b.seller || '')
        },
        {
            title: 'Asesor que evaluó',
            dataIndex: 'asesor',
            key: 'asesor',
            render: (text, record) => (
                <div className="flex flex-col">
                    <span className="text-sm text-surface-800">{emptyDash(text)}</span>
                    {record.rol ? <span className="text-xs text-surface-500">{rolLabel(record.rol)}</span> : null}
                </div>
            ),
            sorter: (a, b) => (a.asesor || '').localeCompare(b.asesor || '')
        },
        {
            title: 'Cliente',
            dataIndex: 'cliente',
            key: 'cliente',
            render: (text) => (
                <div className="flex items-center gap-2 text-sm text-surface-700">
                    <Building2 className="shrink-0 text-[13px] text-surface-400" />
                    <span>{emptyDash(text)}</span>
                </div>
            ),
            sorter: (a, b) => (a.cliente || '').localeCompare(b.cliente || '')
        },
        {
            title: 'Canal',
            dataIndex: 'canal',
            key: 'canal',
            render: (text) => (text ? <StatusTag className="capitalize">{text}</StatusTag> : emptyDash(text)),
            sorter: (a, b) => (a.canal || '').localeCompare(b.canal || '')
        },
        {
            title: 'Región',
            dataIndex: 'regions',
            key: 'regions',
            render: (regions) =>
                Array.isArray(regions) && regions.length ? (
                    <div className="flex flex-wrap gap-1">
                        {regions.map((r) => (
                            <StatusTag key={r} tone="info" icon={<MapPin />}>
                                {r}
                            </StatusTag>
                        ))}
                    </div>
                ) : (
                    emptyDash(null)
                )
        },
        {
            title: 'Resultado',
            dataIndex: 'percentage',
            key: 'percentage',
            align: 'right',
            render: (text, record) => {
                const value = parseFloat(text) || 0;
                const tone = scoreTone(text);
                return (
                    <div className="flex flex-col items-end gap-1">
                        <div className="flex items-center gap-2">
                            <Progress percent={value} size="small" showInfo={false} strokeColor={semantic[tone].solid} className="!m-0 w-[70px]" />
                            <StatusTag tone={tone} icon={value >= 70 ? <CircleCheck /> : null} className="min-w-[60px] justify-center tabular-nums">
                                {value.toFixed(1)}%
                            </StatusTag>
                        </div>
                        <span className="text-xs text-surface-500 tabular-nums">{record.answers}</span>
                    </div>
                );
            },
            sorter: (a, b) => (parseFloat(a.percentage) || 0) - (parseFloat(b.percentage) || 0)
        },
        {
            title: 'Fecha',
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (text) => (
                <div className="flex items-center gap-2 text-sm text-surface-600 tabular-nums">
                    <Calendar className="shrink-0 text-[13px] text-surface-400" />
                    <span>{text ? dayjs(text).format('DD/MM/YYYY') : emptyDash(null)}</span>
                </div>
            ),
            sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
            defaultSortOrder: 'descend'
        },
        {
            title: 'Acciones',
            key: 'actions',
            align: 'right',
            width: 88,
            render: (_, record) => (
                <IconActions>
                    <IconAction
                        icon={<Trash2 />}
                        label="Eliminar encuesta"
                        danger
                        onClick={() => onDelete?.(record._id)}
                        confirm={{
                            title: 'Eliminar encuesta',
                            description: '¿Seguro que deseas eliminar esta encuesta? Esta acción no se puede deshacer.',
                            okText: 'Eliminar',
                            cancelText: 'Cancelar'
                        }}
                    />
                </IconActions>
            )
        }
    ];

    return (
        <SectionCard
            title="Encuestas"
            description={`${polls?.length || 0} resultados · despliega una fila para ver las respuestas`}
            icon={<ClipboardList />}
            flush
            bodyClassName="[&_.ant-pagination]:px-5">
            <Table
                loading={loading}
                dataSource={polls}
                // Without a delete handler (a backend that can't delete polls) there is no actions column.
                columns={onDelete ? PollColumns : PollColumns.filter((column) => column.key !== 'actions')}
                size="middle"
                scroll={{ x: 'max-content' }}
                expandable={{
                    expandedRowRender: (record) => <PollDetail record={record} />,
                    rowExpandable: (record) => Array.isArray(record.values) && record.values.length > 0
                }}
                locale={{ emptyText: <EmptyState icon={<ClipboardList />} title="No hay encuestas para los filtros seleccionados" /> }}
                pagination={{
                    pageSize: 20,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} encuestas`
                }}
                rowKey="key"
            />
        </SectionCard>
    );
}
